// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/hooks/useOrdemServicoDetalhe.ts
//  Hooks dos sub-recursos da OS: equipamentos, técnicos executores e
//  acompanhamento de status. Habilitados apenas em modo edição (OS já criada).
// ════════════════════════════════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import { ordemServicoService } from '../services/ordemServicoService';
import type {
  OsEquipamentoRequest,
  OsTecnicoRequest,
  OsAcompanhamentoRequest,
  OsOcorrenciaRequest,
  OsTarefaRequest,
  OsDespesaRequest,
} from '../types';

// ── Equipamentos ────────────────────────────────────────────────────────────

export const useOsEquipamentos = (osId?: number) => {
  const queryClient = useQueryClient();
  const enabled = !!osId;

  const { data: equipamentos = [], isLoading } = useQuery({
    queryKey: ['os-equipamentos', osId],
    queryFn: () => ordemServicoService.getEquipamentos(osId!),
    enabled,
  });

  const salvar = useMutation({
    mutationFn: (itens: OsEquipamentoRequest[]) => ordemServicoService.saveEquipamentos(osId!, itens),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['os-equipamentos', osId] });
      toast.success('Equipamentos gravados');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return { equipamentos, isLoading, salvarEquipamentos: salvar.mutate, isSaving: salvar.isPending };
};

// ── Técnicos executores ──────────────────────────────────────────────────────

export const useOsTecnicos = (osId?: number) => {
  const queryClient = useQueryClient();
  const enabled = !!osId;

  const { data: tecnicos = [], isLoading } = useQuery({
    queryKey: ['os-tecnicos', osId],
    queryFn: () => ordemServicoService.getTecnicos(osId!),
    enabled,
  });

  const salvar = useMutation({
    mutationFn: (itens: OsTecnicoRequest[]) => ordemServicoService.saveTecnicos(osId!, itens),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['os-tecnicos', osId] });
      toast.success('Técnicos gravados');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return { tecnicos, isLoading, salvarTecnicos: salvar.mutate, isSaving: salvar.isPending };
};

// ── Acompanhamento / Status ───────────────────────────────────────────────────

export const useOsAcompanhamentos = (osId?: number) => {
  const queryClient = useQueryClient();
  const enabled = !!osId;

  const { data: acompanhamentos = [], isLoading } = useQuery({
    queryKey: ['os-acompanhamentos', osId],
    queryFn: () => ordemServicoService.getAcompanhamentos(osId!),
    enabled,
  });

  const adicionar = useMutation({
    mutationFn: (req: OsAcompanhamentoRequest) => ordemServicoService.addAcompanhamento(osId!, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['os-acompanhamentos', osId] });
      toast.success('Status atualizado');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return {
    acompanhamentos,
    isLoading,
    adicionarAcompanhamento: adicionar.mutate,
    isSaving: adicionar.isPending,
  };
};

// ── Ocorrências ──────────────────────────────────────────────────────────────

export const useOsOcorrencias = (osId?: number) => {
  const queryClient = useQueryClient();
  const enabled = !!osId;

  const { data: ocorrencias = [], isLoading } = useQuery({
    queryKey: ['os-ocorrencias', osId],
    queryFn: () => ordemServicoService.getOcorrencias(osId!),
    enabled,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['os-ocorrencias', osId] });

  const adicionar = useMutation({
    mutationFn: (req: OsOcorrenciaRequest) => ordemServicoService.addOcorrencia(osId!, req),
    onSuccess: () => { invalidate(); toast.success('Ocorrência registrada'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const remover = useMutation({
    mutationFn: (ocorrenciaId: number) => ordemServicoService.deleteOcorrencia(osId!, ocorrenciaId),
    onSuccess: () => { invalidate(); toast.success('Ocorrência removida'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return {
    ocorrencias,
    isLoading,
    adicionarOcorrencia: adicionar.mutate,
    removerOcorrencia: remover.mutate,
    isSaving: adicionar.isPending,
  };
};

// ── Tarefas / checklist ────────────────────────────────────────────────────────

export const useOsTarefas = (osId?: number) => {
  const queryClient = useQueryClient();
  const enabled = !!osId;

  const { data: tarefas = [], isLoading } = useQuery({
    queryKey: ['os-tarefas', osId],
    queryFn: () => ordemServicoService.getTarefas(osId!),
    enabled,
  });

  const salvar = useMutation({
    mutationFn: (itens: OsTarefaRequest[]) => ordemServicoService.saveTarefas(osId!, itens),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['os-tarefas', osId] });
      toast.success('Checklist gravado');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return { tarefas, isLoading, salvarTarefas: salvar.mutate, isSaving: salvar.isPending };
};

// ── Despesas ─────────────────────────────────────────────────────────────────

export const useOsDespesas = (osId?: number) => {
  const queryClient = useQueryClient();
  const enabled = !!osId;

  const { data: despesas = [], isLoading } = useQuery({
    queryKey: ['os-despesas', osId],
    queryFn: () => ordemServicoService.getDespesas(osId!),
    enabled,
  });

  const salvar = useMutation({
    mutationFn: (itens: OsDespesaRequest[]) => ordemServicoService.saveDespesas(osId!, itens),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['os-despesas', osId] });
      toast.success('Despesas gravadas');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return { despesas, isLoading, salvarDespesas: salvar.mutate, isSaving: salvar.isPending };
};
